import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Tooltips for every element with `data-tip` (side from `data-tip-pos`). One bubble is drawn in a
// body-level layer at fixed coordinates: a pseudo-element inside a glass panel (its own stacking
// context) went under the next panel, and one inside a scroll container was clipped by it.

export type TipPos = 'top' | 'right' | 'below' | 'left';

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

const GAP = 8;
const SIDE_GAP = 10;
const MARGIN = 8;
/** Sweeping the pointer across the rail shouldn't flash every label; once one shows, the next shows at once. */
const HOVER_DELAY_MS = 120;

/**
 * Where the bubble goes: on the asked-for side when it fits there, on the opposite side when only
 * that fits, then kept inside the viewport. `left` keeps its old meaning: above, aligned to the
 * anchor's right edge and growing leftwards.
 */
export function placeTip(anchor: Box, size: { width: number; height: number }, viewport: { width: number; height: number }, pos: TipPos) {
  const { width, height } = size;
  let left: number;
  let top: number;
  if (pos === 'right') {
    left = anchor.right + SIDE_GAP;
    if (left + width > viewport.width - MARGIN && anchor.left - SIDE_GAP - width >= MARGIN) left = anchor.left - SIDE_GAP - width;
    top = anchor.top + anchor.height / 2 - height / 2;
  } else {
    const above = anchor.top - GAP - height;
    const below = anchor.bottom + GAP;
    const fitsAbove = above >= MARGIN;
    const fitsBelow = below + height <= viewport.height - MARGIN;
    top = pos === 'below' ? (fitsBelow || !fitsAbove ? below : above) : fitsAbove || !fitsBelow ? above : below;
    left = pos === 'left' ? anchor.right - width : anchor.left + anchor.width / 2 - width / 2;
  }
  return {
    left: Math.round(Math.max(MARGIN, Math.min(left, viewport.width - width - MARGIN))),
    top: Math.round(Math.max(MARGIN, Math.min(top, viewport.height - height - MARGIN))),
  };
}

function tipAnchor(target: EventTarget | null): HTMLElement | null {
  const el = target instanceof Element ? target.closest<HTMLElement>('[data-tip]') : null;
  return el?.dataset.tip ? el : null;
}

function tipPos(el: HTMLElement): TipPos {
  const pos = el.dataset.tipPos;
  return pos === 'right' || pos === 'below' || pos === 'left' ? pos : 'top';
}

/**
 * Shows the tooltip of the hovered element (mouse or pen, not touch) and of the element focused from
 * the keyboard, like `:focus-visible`. A click hides it until the pointer leaves that element; Escape,
 * scrolling and resizing hide it. The bubble repeats the control's visible or accessible name, so it
 * stays out of the accessibility tree.
 */
export function TooltipHost() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [text, setText] = useState('');
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timer = 0;
    let shown: HTMLElement | null = null;
    let shownBy: 'pointer' | 'focus' = 'pointer';
    let suppressed: HTMLElement | null = null;
    let fromKeyboard = false;
    const show = (el: HTMLElement, by: 'pointer' | 'focus') => {
      window.clearTimeout(timer);
      shown = el;
      shownBy = by;
      setText(el.dataset.tip ?? '');
      setAnchor(el);
    };
    const hide = () => {
      window.clearTimeout(timer);
      if (!shown) return;
      shown = null;
      setAnchor(null);
    };
    const onOver = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      const el = tipAnchor(event.target);
      if (el !== suppressed) suppressed = null;
      if (el === shown) return;
      if (!el || el === suppressed) {
        if (shownBy === 'pointer') hide();
        else window.clearTimeout(timer);
        return;
      }
      window.clearTimeout(timer);
      if (shown) show(el, 'pointer');
      else timer = window.setTimeout(() => show(el, 'pointer'), HOVER_DELAY_MS);
    };
    const onOut = (event: PointerEvent) => {
      // Leaving the window: no pointerover follows.
      if (!event.relatedTarget && shownBy === 'pointer') hide();
    };
    const onDown = (event: PointerEvent) => {
      fromKeyboard = false;
      suppressed = tipAnchor(event.target);
      hide();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
      else fromKeyboard = true;
    };
    const onFocusIn = (event: FocusEvent) => {
      const el = event.target instanceof HTMLElement && event.target.dataset.tip ? event.target : null;
      if (el && fromKeyboard) show(el, 'focus');
      else if (shown && shownBy === 'focus') hide();
    };
    const onFocusOut = (event: FocusEvent) => {
      if (event.target === shown && shownBy === 'focus') hide();
    };
    document.addEventListener('pointerover', onOver);
    document.addEventListener('pointerout', onOut);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerover', onOver);
      document.removeEventListener('pointerout', onOut);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, []);

  // While shown, follow the anchor's text, and go away with it when it leaves the page (a deleted message).
  useEffect(() => {
    if (!anchor) return undefined;
    const observer = new MutationObserver(() => setText(anchor.dataset.tip ?? ''));
    observer.observe(anchor, { attributes: true, attributeFilter: ['data-tip'] });
    const check = window.setInterval(() => {
      if (!anchor.isConnected || !anchor.dataset.tip) setAnchor(null);
    }, 400);
    return () => {
      observer.disconnect();
      window.clearInterval(check);
    };
  }, [anchor]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!anchor || !el) {
      setPlace(null);
      return;
    }
    const next = placeTip(anchor.getBoundingClientRect(), { width: el.offsetWidth, height: el.offsetHeight }, { width: window.innerWidth, height: window.innerHeight }, tipPos(anchor));
    setPlace((prev) => (prev && prev.left === next.left && prev.top === next.top ? prev : next));
  }, [anchor, text]);

  if (!anchor || !text) return null;
  return createPortal(
    <div ref={ref} className="tip" aria-hidden="true" style={place ? { left: place.left, top: place.top } : { left: -9999, top: -9999 }}>
      {text}
    </div>,
    document.body,
  );
}
